/**
 * PATH: packages/ui/src/contextMenu/usePositionedMenuKeyboard.ts
 * FILE_ID: UI-CTXMENU-KEYBOARD-001
 * PURPOSE: Give positioned menus one shared focus and keyboard contract.
 * ROLE IN ARCHITECTURE: Shared UI behavior hook for compound context menus.
 * MAIN EXPORTS: usePositionedMenuKeyboard.
 * NON-RESPONSIBILITIES: Geometry, rendering, actions, and submenu state.
 * NOTES FOR FUTURE AI: Keep selectors aligned with ContextMenuItem role/disabled.
 */
import { type RefObject, useEffect, useRef } from 'react';

interface PositionedMenuKeyboardInput {
  menuRef: RefObject<HTMLDivElement | null>;
  onClose: () => void;
  restoreFocusTo?: HTMLElement | null;
}

function enabledMenuItems(menu: HTMLElement): HTMLElement[] {
  return Array.from(
    menu.querySelectorAll<HTMLElement>(
      '[role="menuitem"]:not([disabled]):not([aria-disabled="true"])'
    )
  );
}

function moveFocus(items: HTMLElement[], current: Element | null, delta: number): void {
  if (items.length === 0) return;
  const currentIndex = items.findIndex((item) => item === current);
  const nextIndex =
    currentIndex < 0
      ? delta > 0
        ? 0
        : items.length - 1
      : (currentIndex + delta + items.length) % items.length;
  items[nextIndex]?.focus();
}

/**
 * Focuses the first enabled item and implements menu keyboard navigation.
 *
 * Inputs: mounted menu ref, close callback, and optional trigger to restore.
 * Output: no value. Side effects: DOM focus and keydown listener.
 * Failure mode: an empty menu keeps focus on its panel and still supports Escape.
 */
export function usePositionedMenuKeyboard({
  menuRef,
  onClose,
  restoreFocusTo,
}: PositionedMenuKeyboardInput): void {
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    const menu = menuRef.current;
    if (!menu) return;
    const focusTarget = restoreFocusTo ?? (document.activeElement as HTMLElement | null);
    let mounted = true;
    queueMicrotask(() => {
      if (!mounted) return;
      const items = enabledMenuItems(menu);
      (items[0] ?? menu).focus();
    });

    const handleKeyDown = (event: KeyboardEvent) => {
      const items = enabledMenuItems(menu);
      if (event.key === 'Escape') {
        event.preventDefault();
        onCloseRef.current();
        return;
      }
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        moveFocus(items, document.activeElement, 1);
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        moveFocus(items, document.activeElement, -1);
      } else if (event.key === 'Home') {
        event.preventDefault();
        items[0]?.focus();
      } else if (event.key === 'End') {
        event.preventDefault();
        items[items.length - 1]?.focus();
      } else if (event.key === 'Tab') {
        event.preventDefault();
        moveFocus(items, document.activeElement, event.shiftKey ? -1 : 1);
      }
    };

    menu.addEventListener('keydown', handleKeyDown);
    return () => {
      mounted = false;
      menu.removeEventListener('keydown', handleKeyDown);
      if (focusTarget?.isConnected) focusTarget.focus();
    };
  }, [menuRef, restoreFocusTo]);
}
